/**
 * Care Monitor Service
 * 
 * API service for the AC Vitals Monitor (Aged Care Patient Monitoring System).
 * Handles API calls for aged care patient vitals, analytics, and notes.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

class CareMonitorService {
  /**
   * Get all aged care patients with today's vitals
   * @returns {Promise<Object>} Patients with vitals and status
   */
  async getAgedCarePatients() {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/care-monitor/aged-care-patients`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch aged care patients: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching aged care patients:', error);
      throw error;
    }
  }

  /**
   * Get detailed vitals for a specific patient
   * @param {string} patientId - Patient ID (string format, e.g., "PAT001")
   * @param {string} dateRange - Date range: "today", "7days", or "30days"
   * @returns {Promise<Object>} Patient vitals detail
   */
  async getPatientVitalsDetail(patientId, dateRange = 'today') {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/care-monitor/patient-vitals/${patientId}?date_range=${dateRange}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch patient vitals: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching patient vitals detail:', error);
      throw error;
    }
  }

  /**
   * Add admin notes to patient vitals for a specific date
   * @param {number} patientId - Patient numeric ID
   * @param {string} date - Date (YYYY-MM-DD format)
   * @param {string} notes - Admin notes
   * @returns {Promise<Object>} Response with success status
   */
  async addVitalsNotes(patientId, date, notes) {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/care-monitor/vitals-notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patient_id: patientId,
          recorded_date: date,  // Backend expects 'recorded_date' not 'date'
          notes: notes
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to add vitals notes: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding vitals notes:', error);
      throw error;
    }
  }

  /**
   * Get analytics data for a specific vital type
   * @param {number} patientId - Patient numeric ID
   * @param {string} vitalType - Vital type (e.g., "systolic_bp", "pulse_rate")
   * @param {string} dateRange - Date range: "today", "7days", or "30days"
   * @returns {Promise<Object>} Analytics data for graphing
   */
  async getVitalsAnalytics(patientId, vitalType, dateRange = 'today') {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/care-monitor/vitals-analytics?patient_id=${patientId}&vital_type=${vitalType}&date_range=${dateRange}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch vitals analytics: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching vitals analytics:', error);
      throw error;
    }
  }
}

const careMonitorService = new CareMonitorService();
export default careMonitorService;

